<template>
  <section id="user-detail">
    <v-row>
      <v-col cols="12" md="12" lg="12">
        <page-header
          class="mb-8"
          :breadcrumbs="breadcrumbs"
          :title="user.email"
        />
        <v-tabs
          v-model="activeTab"
          background-color="transparent"
          class="border-bottom-grey"
        >
          <v-tab
            class="px-12 subtitle-2 text-dark-grey font-weight-large"
            active-class="setting-active-tab"
            v-for="({ name, query, elementId }, i) in tabsName"
            :id="elementId"
            v-text="name"
            :key="i"
            @click="onTabChange(query, i)"
          >
          </v-tab>
        </v-tabs>
        <v-row class="d-flex justify-center">
          <v-col cols="12">
            <v-tabs-items
              v-model="activeTab"
              class="border-bottom-2 transparent"
            >
              <v-tab-item
                v-for="itemComponent in tabItems"
                :key="itemComponent"
              >
                <keep-alive>
                  <component
                    :is="itemComponent"
                    :user="user"
                    :is-loading="isLoading"
                  />
                </keep-alive>
              </v-tab-item>
            </v-tabs-items>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import { mapActions } from "vuex";
import { getUser } from "@/services";
import UserProfile from "./UserProfile.vue";

import UserCustomers from "./UserCustomers.vue";
import { PageHeader } from "@/components/shared";
import UserAccountAccess from "./UserAccountAccess.vue";

import { TabsNavigatorMixin, TitleMixin, SharedUtilsMixin } from "@/mixins";

/**
 * User details view
 */
export default {
  name: "User",
  /**
   *   Page title
   */
  title: "User",
  /**
   * -------------- Mixins -------------
   */
  mixins: [TitleMixin, TabsNavigatorMixin, SharedUtilsMixin],
  /**
   * ---------------- Components ------------------
   */
  components: {
    PageHeader,
    UserProfile,
    UserAccountAccess,
    UserCustomers,
  },
  /**
   * ---------------- Data Properties ------------------
   */
  data() {
    return {
      user: {
        roles: [],
      },
      activeTab: 0,
      isLoading: false,
      tabsName: [
        {
          name: "Profile",
          query: "profile",
          elementId: "user_profile_setting",
        },
        {
          name: "Accounts",
          query: "user_accounts",
          elementId: "user_accounts",
        },
        {
          name: "Customers",
          query: "user_customers",
          elementId: "user_customers",
        },
      ],
      tabItems: ["UserProfile", "UserAccountAccess", "UserCustomers"],
    };
  },
  /**
   * ---------------- Computed properties ------------------
   */
  computed: {
    /**
     * User view breadcrumbs
     */
    breadcrumbs() {
      return [
        {
          title: this.$dictionary.app.manageUsers.index.title,
          type: this.$appConfig.breadcrumbs.types.route,
          to: this.$paths.users,
        },
        {
          title: this.user?.email ?? "",
          type: this.$appConfig.breadcrumbs.types.route,
        },
      ];
    },
    /**
     * Id of the user whose details are to be viewed
     * @type {String}
     */
    userId() {
      return this.$route.params?.id;
    },
  },
  /**
   * ---------------- Methods ------------------
   */
  methods: {
    /**
     * Maps vuex store actions in the store
     */
    ...mapActions({
      setSnackbar: "ui/setSnackbar",
    }),
    /**
     * Fetches user details
     */
    async getUserDetails() {
      try {
        this.isLoading = true;
        const { data } = await getUser(this.userId);
        this.user = data;
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: this.$errorMessage(error),
          type: this.snackbarTypes.error,
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  /**
   * ---------------- Created lifecycle hook ------------------
   */
  async created() {
    await this.getUserDetails();

    this.$options.title = `User - ${this.user?.email}`;
    this.updateTitle();
  },
};
</script>
